// Helper puro: dado uma activity com `google_event_id`, dispara a invocação
// do edge function `calendar-sync` (delete_event) e classifica o resultado.
//
// 1) Sem `googleEventId`: no-op (atividade não tinha evento no Google).
// 2) Invoke OK + status 200/201/204/404/410/"skipped": sucesso silencioso.
// 3) Invoke OK + status >=400 (que não seja 404/410): warning não-bloqueante.
// 4) Invoke erro: warning não-bloqueante (não impede o delete da activity).

export type CalendarSyncDeleteResponse =
  | { data: { google_status?: number | string; deleted?: boolean } | null; error: null }
  | { data: null; error: { message?: string } }
  | { data: null; error: unknown };

export interface DeleteGoogleEventArgs {
  activityId: string;
  googleEventId: string | null | undefined;
  invokeCalendarSync: (body: {
    action: 'delete_event';
    activity_id: string;
  }) => Promise<CalendarSyncDeleteResponse>;
  onWarning?: (msg: string) => void;
}

export type DeleteGoogleEventOutcome =
  | { kind: 'skipped_no_google_event' }
  | { kind: 'deleted'; status: number | string }
  | { kind: 'silent_not_found'; status: number | string }
  | { kind: 'warned'; reason: string };

const BENIGN_NOT_FOUND = new Set<number | string>([404, 410, '404', '410', 'skipped']);

export async function deleteGoogleEventForActivity({
  activityId,
  googleEventId,
  invokeCalendarSync,
  onWarning,
}: DeleteGoogleEventArgs): Promise<DeleteGoogleEventOutcome> {
  if (!googleEventId) return { kind: 'skipped_no_google_event' };

  try {
    const { data, error } = await invokeCalendarSync({
      action: 'delete_event',
      activity_id: activityId,
    });
    if (error) {
      const msg = (error as { message?: string }).message || 'unknown calendar-sync error';
      onWarning?.(msg);
      return { kind: 'warned', reason: msg };
    }
    const status = (data?.google_status as number | string | undefined) ?? 200;
    if (BENIGN_NOT_FOUND.has(status)) {
      return { kind: 'silent_not_found', status };
    }
    if (typeof status === 'number' && status >= 400) {
      const reason = `google_status=${status}`;
      onWarning?.(reason);
      return { kind: 'warned', reason };
    }
    return { kind: 'deleted', status };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    onWarning?.(reason);
    return { kind: 'warned', reason };
  }
}
